import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EDITABLE_FILE_TYPE_OPTIONS, SUBCATEGORY_BY_FILE_TYPE } from './fileTypes';
import type { Asset, RenameModalExtrasContext } from './types';

interface RenameFileModalProps {
    asset: Asset;
    isRenaming: boolean;
    error: string | null;
    /** Allow changing the coarse category. When false the select is locked. */
    canReclassify?: boolean;
    /** Host-provided slot rendered between the form fields and the metadata
     *  accordion (e.g. the files-viewer CBCT processing trigger). */
    extras?: (ctx: RenameModalExtrasContext) => React.ReactNode;
    onConfirm: (changes: {
        newName?: string;
        fileType?: string;
        metadata?: Record<string, unknown>;
    }) => void;
    onCancel: () => void;
}

// Split a filename into its basename and extension. We treat the LAST dot as
// the extension separator so `foo.bar.zip` → ("foo.bar", "zip"). Files with no
// extension (or hidden files like ".env") return their full name as basename
// and an empty extension — the input then edits the whole string.
function splitName(name: string): { base: string; ext: string } {
    const idx = name.lastIndexOf('.');
    if (idx <= 0 || idx === name.length - 1) {
        return { base: name, ext: '' };
    }
    return { base: name.slice(0, idx), ext: name.slice(idx + 1) };
}

function humanizeKey(key: string): string {
    const spaced = key
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function formatMetaValue(value: unknown): string {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}

const RenameFileModal: React.FC<RenameFileModalProps> = ({
    asset,
    isRenaming,
    error,
    canReclassify = false,
    extras,
    onConfirm,
    onCancel
}) => {
    const categorySelectLocked = !canReclassify;

    const { base: initialBase, ext } = useMemo(() => splitName(asset.name), [asset.name]);
    const [base, setBase] = useState(initialBase);
    const initialFileType = useMemo(
        () =>
            EDITABLE_FILE_TYPE_OPTIONS.some((o) => o.value === asset.storageCategory)
                ? (asset.storageCategory as string)
                : '',
        [asset.storageCategory]
    );
    const [fileType, setFileType] = useState(initialFileType);
    const inputRef = useRef<HTMLInputElement>(null);

    const subConfig = fileType ? SUBCATEGORY_BY_FILE_TYPE[fileType] : undefined;

    const subValueFromMeta = useCallback(
        (ft: string): string => {
            const cfg = ft ? SUBCATEGORY_BY_FILE_TYPE[ft] : undefined;
            if (!cfg) return '';
            const v = asset.metadata?.[cfg.metaKey];
            return typeof v === 'string' && cfg.options.some((o) => o.value === v) ? v : '';
        },
        [asset.metadata]
    );

    const [subValue, setSubValue] = useState(() => subValueFromMeta(initialFileType));

    const metadataEntries = useMemo(
        () => Object.entries(asset.metadata ?? {}).filter(([k]) => k !== subConfig?.metaKey),
        [asset.metadata, subConfig?.metaKey]
    );
    const [metadataOpen, setMetadataOpen] = useState(false);

    // Focus + select the basename on open so the user can start typing
    // immediately. Re-runs if the modal opens for a different asset.
    useEffect(() => {
        setBase(initialBase);
        setFileType(initialFileType);
        setSubValue(subValueFromMeta(initialFileType));
        const id = window.setTimeout(() => {
            inputRef.current?.focus();
            inputRef.current?.select();
        }, 0);
        return () => window.clearTimeout(id);
    }, [asset.assetId, initialBase, initialFileType, subValueFromMeta]);

    const handleFileTypeChange = (next: string) => {
        setFileType(next);
        setSubValue(subValueFromMeta(next));
    };

    const trimmedBase = base.trim();
    const newName = ext ? `${trimmedBase}.${ext}` : trimmedBase;
    const nameChanged = trimmedBase.length > 0 && newName !== asset.name;
    const typeChanged = fileType !== '' && fileType !== initialFileType;
    const subChanged = !!subConfig && subValue !== subValueFromMeta(fileType);
    const canSubmit = (nameChanged || typeChanged || subChanged) && !isRenaming;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        onConfirm({
            ...(nameChanged ? { newName } : {}),
            ...(typeChanged ? { fileType } : {}),
            ...(subChanged && subConfig
                ? { metadata: { [subConfig.metaKey]: subValue || null } }
                : {})
        });
    };

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={isRenaming ? undefined : onCancel}
        >
            <div
                className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 relative"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header: title + close, design-system standard. */}
                <div className="flex items-center justify-between mb-5">
                    <h3 className="text-xl font-bold text-gray-900">Edit file</h3>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isRenaming}
                        className="text-gray-400 hover:text-gray-800 transition-colors disabled:opacity-50"
                        aria-label="Close"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Filename with locked extension. Yellow ring on focus is the
              app's "active selection" accent (matches SDI radio + badges). */}
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Name</label>
                    <div className="flex items-stretch rounded-lg border-2 border-gray-200 bg-white overflow-hidden transition-colors focus-within:border-yellow-400">
                        <input
                            ref={inputRef}
                            type="text"
                            value={base}
                            onChange={(e) => setBase(e.target.value)}
                            disabled={isRenaming}
                            maxLength={ext ? 200 - (ext.length + 1) : 200}
                            className="flex-1 min-w-0 px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 outline-none disabled:bg-gray-50 disabled:cursor-not-allowed"
                            placeholder="filename"
                        />
                        {ext && (
                            <span className="flex-shrink-0 inline-flex items-center px-3 text-sm text-gray-500 bg-gray-50 border-l border-gray-200 select-none">
                                .{ext}
                            </span>
                        )}
                    </div>

                    {/* Coarse category. Native select styled to match the name input:
              same border/rounding, yellow focus accent, custom chevron. */}
                    <label className="block text-xs font-semibold text-gray-700 mt-4 mb-1.5">
                        Category
                    </label>
                    <div className="relative rounded-lg border-2 border-gray-200 bg-white transition-colors focus-within:border-yellow-400">
                        <select
                            value={fileType}
                            onChange={(e) => handleFileTypeChange(e.target.value)}
                            disabled={isRenaming || categorySelectLocked}
                            className="appearance-none w-full bg-transparent px-3 py-2.5 pr-9 text-sm text-gray-900 outline-none disabled:bg-gray-50 disabled:cursor-not-allowed cursor-pointer"
                        >
                            {initialFileType === '' && (
                                <option value="" disabled>
                                    Select a category
                                </option>
                            )}
                            {EDITABLE_FILE_TYPE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>

                    {/* Subcategory — only for categories with a defined vocabulary. Stored
              in metadata (photoType/documentType). "None" clears it. */}
                    {subConfig && (
                        <>
                            <label className="block text-xs font-semibold text-gray-700 mt-4 mb-1.5">
                                Subcategory
                            </label>
                            <div className="relative rounded-lg border-2 border-gray-200 bg-white transition-colors focus-within:border-yellow-400">
                                <select
                                    value={subValue}
                                    onChange={(e) => setSubValue(e.target.value)}
                                    disabled={isRenaming}
                                    className="appearance-none w-full bg-transparent px-3 py-2.5 pr-9 text-sm text-gray-900 outline-none disabled:bg-gray-50 disabled:cursor-not-allowed cursor-pointer"
                                >
                                    <option value="">None</option>
                                    {subConfig.options.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            </div>
                        </>
                    )}

                    {/* Host extras (e.g. CBCT processing trigger) — capability-injected
              so app-specific flows stay app-side. */}
                    {extras?.({ asset, fileType, initialFileType, isSaving: isRenaming })}

                    {/* Read-only metadata accordion. Shown for reference only — the fields
              above are the only editable ones. Hidden entirely when the asset
              carries no metadata so the modal stays compact. */}
                    {metadataEntries.length > 0 && (
                        <div className="mt-4 rounded-lg border border-gray-200 overflow-hidden">
                            <button
                                type="button"
                                onClick={() => setMetadataOpen((open) => !open)}
                                aria-expanded={metadataOpen}
                                className="flex w-full items-center justify-between gap-2 px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
                            >
                                <span className="text-xs font-semibold text-gray-700">
                                    Metadata
                                    <span className="ml-1.5 font-normal text-gray-400">
                                        ({metadataEntries.length})
                                    </span>
                                </span>
                                <ChevronDownIcon
                                    className={`w-4 h-4 text-gray-400 transition-transform ${metadataOpen ? 'rotate-180' : ''}`}
                                />
                            </button>
                            {metadataOpen && (
                                <dl className="divide-y divide-gray-200 border-t border-gray-200">
                                    {metadataEntries.map(([key, value]) => (
                                        <div key={key} className="flex items-start gap-3 px-3 py-2">
                                            <dt className="flex-shrink-0 w-1/3 min-w-0 text-xs font-medium text-gray-500 truncate">
                                                {humanizeKey(key)}
                                            </dt>
                                            <dd className="flex-1 min-w-0 text-xs text-gray-700 break-words font-mono">
                                                {formatMetaValue(value)}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            )}
                        </div>
                    )}

                    {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

                    <div className="flex justify-end items-center gap-3 mt-6">
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={isRenaming}
                            className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={!canSubmit}
                            className="px-4 py-2 text-sm font-semibold text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                        >
                            {isRenaming ? (
                                <>
                                    <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                'Save'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export { RenameFileModal };
